import type { ApiResponse, PaginationParams } from '#shared/types/api'
import type { Database } from '#shared/types/database.types'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

// Database types
type Location = Database['public']['Tables']['vzw_locations']['Row']
type LocationInsert = Database['public']['Tables']['vzw_locations']['Insert']
type LocationUpdate = Database['public']['Tables']['vzw_locations']['Update']

interface LocationsQueryParams extends PaginationParams {
  search?: string
  isActive?: boolean
  city?: string
  postalCode?: string
}

export const useLocations = () => {
  /**
   * Fetch all locations with optional filters
   */
  const getLocations = async (params?: LocationsQueryParams) => {
    try {
      const queryString = buildQueryString(params || {})
      const url = `/api/camps/locations${queryString ? `?${queryString}` : ''}`

      return await $api<ApiResponse<Location[]>>(url, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError('/api/camps/locations', error, { params })
      throw error
    }
  }

  /**
   * Fetch a single location by ID
   */
  const getLocation = async (id: string) => {
    try {
      return await $api<ApiResponse<Location>>(`/api/camps/locations/${id}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError(`/api/camps/locations/${id}`, error)
      throw error
    }
  }

  /**
   * Create a new location
   * Requires: camps:manage permission
   */
  const createLocation = async (location: LocationInsert) => {
    try {
      return await $api<ApiResponse<Location>>('/api/camps/locations', {
        method: 'POST',
        body: location,
      })
    }
    catch (error) {
      logApiError('/api/camps/locations', error, { location })
      throw error
    }
  }

  /**
   * Update an existing location
   * Requires: camps:manage permission
   */
  const updateLocation = async (id: string, updates: LocationUpdate) => {
    try {
      return await $api<ApiResponse<Location>>(`/api/camps/locations/${id}`, {
        method: 'PATCH',
        body: updates,
      })
    }
    catch (error) {
      logApiError(`/api/camps/locations/${id}`, error, { updates })
      throw error
    }
  }

  /**
   * Delete a location
   * Requires: camps:manage permission
   * Note: Cannot delete locations that are assigned to camps
   */
  const deleteLocation = async (id: string) => {
    try {
      return await $api<ApiResponse<{ success: boolean }>>(
        `/api/camps/locations/${id}`,
        {
          method: 'DELETE',
        },
      )
    }
    catch (error) {
      logApiError(`/api/camps/locations/${id}`, error, { action: 'delete' })
      throw error
    }
  }

  /**
   * Fetch only active locations (for public use)
   */
  const getActiveLocations = async () => {
    return getLocations({
      isActive: true,
      limit: 100,
      sort: 'display_order',
      order: 'asc',
    })
  }

  /**
   * Get full location display string (name + city)
   */
  const getLocationDisplay = (location: Location): string => {
    const parts = [location.name]
    if (location.city) {
      parts.push(location.city)
    }

    return parts.join(' - ')
  }

  return {
    // Core CRUD operations
    getLocations,
    getLocation,
    createLocation,
    updateLocation,
    deleteLocation,

    // Convenience methods
    getActiveLocations,
    getLocationDisplay,
  }
}

// Re-export types for external use
export type { Location, LocationInsert, LocationUpdate }
